import React, { useState } from "react";
import {
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Alert,
  AlertTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Link } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HomeIcon from "@mui/icons-material/Home";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PaymentIcon from "@mui/icons-material/Payment";
import StarIcon from "@mui/icons-material/Star";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SupportLayout from "./SupportLayout";

const GettingStarted = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});

  const steps = [
    {
      label: "Create Your Account",
      description: "Sign up and set up your host profile",
      timeEstimate: "5 minutes",
      details: [
        "Click 'Get Started' or 'Sign Up' on the homepage",
        "Choose 'Host' account type",
        "Enter your email and create a strong password",
        "Verify your email address",
        "Complete your profile with business information",
        "Add your company logo and contact details"
      ],
      tips: [
        "Use a professional email address for business communications",
        "Save your verification email - you might need it later",
        "Complete all profile fields to increase trust with cleaners"
      ]
    },
    {
      label: "Add Your Properties",
      description: "List properties you want cleaned",
      timeEstimate: "10 minutes per property",
      details: [
        "Navigate to 'Properties' → 'Add New Property'",
        "Enter property details (address, size, rooms)",
        "Upload high-quality photos of each room",
        "Add special instructions (parking, door codes, pet info)",
        "Set cleaning preferences and standards",
        "Define access instructions for cleaners"
      ],
      tips: [
        "Upload at least 5 photos to attract the best cleaners",
        "Be specific about access instructions to avoid delays",
        "Mark fragile items and special care requirements"
      ]
    },
    {
      label: "Set Up Payment Method",
      description: "Add payment method for automatic billing",
      timeEstimate: "3 minutes",
      details: [
        "Go to 'Settings' → 'Payment Methods'",
        "Add credit/debit card or link bank account",
        "Set default payment method for all properties",
        "Review billing preferences and invoice settings",
        "Enable email receipts for all transactions"
      ],
      tips: [
        "Business cards earn reward points on cleaning expenses",
        "Set up multiple payment methods for backup",
        "Review our pricing structure before first booking"
      ]
    },
    {
      label: "Create Your First Schedule",
      description: "Book your first cleaning service",
      timeEstimate: "5 minutes",
      details: [
        "Click 'Create Schedule' on your dashboard",
        "Select property from your list",
        "Choose date, time, and frequency (one-time or recurring)",
        "Select cleaning type (Standard, Deep, Move-in/out)",
        "Add special instructions for this specific cleaning",
        "Review pricing and confirm booking"
      ],
      tips: [
        "Book at least 48 hours in advance for best cleaner availability",
        "Start with a one-time cleaning to test the service",
        "Use recurring schedules for regular maintenance"
      ]
    },
    {
      label: "Find and Assign Cleaners",
      description: "Choose your preferred cleaning professionals",
      timeEstimate: "10 minutes",
      details: [
        "After scheduling, click 'Choose Cleaner'",
        "Browse available cleaners in your area",
        "Review profiles, ratings, and completion rates",
        "Check their specializations and equipment",
        "Assign cleaner or let our system auto-assign",
        "Add to 'Preferred Cleaners' for future bookings"
      ],
      tips: [
        "Read reviews carefully - they reflect real experiences",
        "Message cleaners with questions before assigning",
        "Build relationships with reliable cleaners"
      ]
    }
  ];

  const handleStepComplete = (step) => {
    setCompletedSteps({
      ...completedSteps,
      [step]: !completedSteps[step]
    });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompletedSteps({});
  };

  const progress = (Object.keys(completedSteps).length / steps.length) * 100;

  const quickLinks = [
    {
      title: "Video Tutorials",
      description: "Watch step-by-step video guides",
      icon: <VideoLibraryIcon />,
      link: "/help/videos",
      color: "#ef4444"
    },
    {
      title: "Downloadable Checklist",
      description: "PDF checklist for new hosts",
      icon: <DescriptionIcon />,
      link: "/downloads/host-checklist.pdf",
      color: "#3b82f6"
    },
    {
      title: "Contact Support",
      description: "24/7 assistance if you get stuck",
      icon: <SupportAgentIcon />,
      link: "/contact",
      color: "#10b981"
    }
  ];

  const checklist = [
    "✓ Account created and verified",
    "✓ Profile completed with business info",
    "✓ At least one property added",
    "✓ Property photos uploaded",
    "✓ Payment method added",
    "✓ First cleaning scheduled",
    "✓ Cleaner assigned",
    "✓ Notification preferences set"
  ];

  return (
    <SupportLayout
      title="Getting Started as a Host"
      category="Host Guide"
      subtitle="Complete step-by-step guide to launch your cleaning service in under 30 minutes"
      breadcrumbs={[
        { label: "Help Center", link: "/help" },
        { label: "Getting Started" }
      ]}
      stats={[
        { value: "30 min", label: "Setup Time" },
        { value: "5 steps", label: "Simple Process" },
        { value: "24/7", label: "Support Available" },
        { value: "100%", label: "Money Back Guarantee" }
      ]}
    >
      {/* Progress Bar */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Your Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}% Complete
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            }
          }}
        />
      </Box>

      {/* Quick Links Banner */}
      <Alert 
        severity="info" 
        sx={{ mb: 4, borderRadius: 2 }}
        icon={<VideoLibraryIcon />}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>Want to learn faster?</AlertTitle>
        Watch our <Link to="/help/videos" style={{ color: theme.palette.primary.main }}>video tutorials</Link> or 
        download our <Link to="/downloads/host-checklist.pdf" style={{ color: theme.palette.primary.main }}>printable checklist</Link>
      </Alert>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        {/* Left Column - Steps */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              Host Setup Checklist
            </Typography>
            
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={completedSteps[index] ? CheckCircleIcon : undefined}
                    StepIconProps={{ 
                      sx: { color: completedSteps[index] ? theme.palette.success.main : 'inherit' }
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {step.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {step.timeEstimate}
                      </Typography>
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {step.description}
                      </Typography>
                      
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                        What you'll do:
                      </Typography>
                      <List dense>
                        {step.details.map((detail, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </ListItemIcon>
                            <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>

                      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          💡 Pro Tips:
                        </Typography>
                        <List dense>
                          {step.tips.map((tip, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <StarIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                              </ListItemIcon>
                              <ListItemText primary={tip} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItem>
                          ))}
                        </List>
                      </Alert>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => handleStepComplete(index)}
                          startIcon={completedSteps[index] ? <CheckCircleIcon /> : <AssignmentIcon />}
                          sx={{ borderRadius: 2 }}
                        >
                          {completedSteps[index] ? 'Mark Completed' : 'Mark as Done'}
                        </Button>
                        <Button onClick={handleNext}>
                          {index === steps.length - 1 ? 'Finish' : 'Next Step'}
                        </Button>
                      </Box>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            
            {activeStep === steps.length && (
              <Paper square elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.success.main }}>
                  🎉 Congratulations! You're ready to go!
                </Typography>
                <Typography variant="body2" paragraph>
                  You've completed all the setup steps. Your first cleaning schedule is active and cleaners can now apply.
                </Typography>
                <Button 
                  variant="contained" 
                  component={Link} 
                  to="/dashboard"
                  startIcon={<ArrowForwardIcon />}
                >
                  Go to Dashboard
                </Button>
              </Paper>
            )}
          </Paper>

          {/* Additional Resources Accordion */}
          <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Additional Resources
            </Typography>
            
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  📋 Printable Host Checklist
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {checklist.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {idx < Object.keys(completedSteps).length ? 
                                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} /> :
                                <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                              }
                              <Typography variant="body2">{item}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 2 }}
                  startIcon={<DescriptionIcon />}
                >
                  Download PDF Checklist
                </Button>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  💰 Understanding Pricing & Fees
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Our pricing is transparent with no hidden fees. Here's what you need to know:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon sx={{ color: theme.palette.success.main }} /></ListItemIcon>
                    <ListItemText 
                      primary="Service Fee: 15% of cleaning cost" 
                      secondary="Covers platform costs, support, and insurance"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon sx={{ color: theme.palette.success.main }} /></ListItemIcon>
                    <ListItemText 
                      primary="Cancellation Fee: Varies by notice period" 
                      secondary="Free if cancelled 24+ hours in advance"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon sx={{ color: theme.palette.success.main }} /></ListItemIcon>
                    <ListItemText 
                      primary="Payment Processing: 2.9% + $0.30" 
                      secondary="Standard Stripe processing fee"
                    />
                  </ListItem>
                </List>
                <Button component={Link} to="/pricing" size="small">
                  View Full Pricing Details →
                </Button>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>

        {/* Right Column - Quick Links & Stats */}
        <Grid item xs={12} md={4}>
          {/* Quick Links Cards */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Quick Resources
          </Typography>
          
          {quickLinks.map((link, idx) => (
            <Card 
              key={idx}
              component={Link}
              to={link.link}
              sx={{ 
                mb: 2, 
                textDecoration: 'none',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateX(4px)' }
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(link.color, 0.1), color: link.color }}>
                  {link.icon}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {link.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {link.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}

          {/* Support Card */}
          <Paper sx={{ p: 3, mt: 3, borderRadius: 3, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <SupportAgentIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Need Help?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Our support team is available 24/7
            </Typography>
            <Button 
              variant="contained" 
              component={Link} 
              to="/contact"
              fullWidth
              sx={{ mb: 1 }}
            >
              Contact Support
            </Button>
            <Button 
              variant="outlined" 
              component={Link} 
              to="/faqs"
              fullWidth
            >
              Browse FAQs
            </Button>
          </Paper>

          {/* Community Stats */}
          <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Join Our Community
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                    5k+
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active Hosts
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                    98%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Satisfaction Rate
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Button 
              variant="outlined" 
              fullWidth 
              sx={{ mt: 2 }}
              component={Link}
              to="/community"
            >
              Join Discussion →
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </SupportLayout>
  );
};

export default GettingStarted;